import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  IconButton,
  Grid,
  Autocomplete,
  Tooltip,
  Chip,
  Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../services/api';
import RegisterPaymentDialog from '../components/RegisterPaymentDialog';
import TextField from '../components/common/TextField';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

type PurchaseStatus = 'Pendiente' | 'En proceso' | 'Recibida';
type ReceptionStatus = 'Pendiente' | 'Parcial' | 'Recibida';
// Tipos sugeridos. Texto libre para que el tenant agregue los suyos.
const PURCHASE_TYPES = ['Reposición', 'Fason', 'Materia prima', 'Otro'];
type PurchasePaymentInstallment = {
  id?: number;
  sequence: number;
  percentage: number;
  paymentMethod: string;
  defermentDays: number;
};
type PurchaseAttachment = {
  id: number;
  fileName: string;
  url: string;
  size: number;
  contentType: string;
};
type PurchaseRequestLite = {
  id: number;
  requestNumber: string;
  requestedBy?: string;
  priority?: string;
  requiredDate?: string | null;
  notes?: string;
  items: { productId?: number; materialId?: number; productName?: string; quantity: number; notes?: string }[];
};

type Purchase = {
  id: number;
  supplierId?: number;
  supplierName?: string;
  reference: string;
  expectedDate: string;
  /** Fecha interna para la que se necesita la mercadería (hereda la del pedido interno). */
  requiredDate?: string | null;
  purchaseRequestId?: number;
  purchaseRequestNumber?: string;
  status: PurchaseStatus;
  receptionStatus?: ReceptionStatus;
  receivedAt?: string | null;
  currency?: string;
  exchangeRate?: number;
  total: number;
  /** Total convertido a pesos con la cotización de la orden. */
  totalLocal?: number;
  paidAmount?: number;
  balance?: number;
  notes?: string;
  type?: string;
  paymentMethod?: string;
  paymentDueDate?: string;
  installments?: PurchasePaymentInstallment[];
  items: PurchaseItem[];
  attachments?: PurchaseAttachment[];
};
type Supplier = { id: number; name: string; cuit?: string; paymentTermDays?: number };
type PurchaseItem = {
  id?: number;
  productId?: number;
  productName?: string;
  materialId?: number;
  materialName?: string;
  receivedQuantity?: number;
  quantity: number;
  unitCost: number;
  ivaRate?: number;
  lotNumber?: string;
  /** Solo UI: de dónde sale la línea (producto, materia prima o texto libre). */
  source?: 'product' | 'material' | 'free';
};

const itemSource = (it: PurchaseItem): 'product' | 'material' | 'free' =>
  it.source ?? (it.productId ? 'product' : it.materialId ? 'material' : 'free');

// Fechas date-only guardadas como medianoche UTC: formatear en UTC para que no se
// corran un día hacia atrás en hora argentina.
const fmtDateUtc = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : '-');
const isPastDate = (iso?: string | null) => !!iso && iso.substring(0, 10) < new Date().toLocaleDateString('en-CA');

// Alícuotas de IVA disponibles en Argentina.
const IVA_RATES = [0, 10.5, 21];
const DEFAULT_IVA_RATE = 21;

// Colores de los cartelitos de estado (como en la demo que vio el cliente).
const STATUS_CHIP_COLOR: Record<PurchaseStatus, 'info' | 'warning' | 'success'> = {
  'Pendiente': 'info',
  'En proceso': 'warning',
  'Recibida': 'success',
};
const RECEPTION_CHIP_COLOR: Record<ReceptionStatus, 'default' | 'warning' | 'success'> = {
  'Pendiente': 'default',
  'Parcial': 'warning',
  'Recibida': 'success',
};

// Una entrega puede traer VARIOS lotes con distinto vencimiento para el mismo
// ítem (pedido de la reunión 2026-08-20): cada línea tiene N filas de lote.
type ReceiveLotRow = { quantity: number; lotNumber: string; expirationDate: string };
type ReceiveLine = {
  purchaseItemId: number;
  materialId?: number;
  rows: ReceiveLotRow[];
};

export const PurchasesPage: React.FC = () => {
  const location = useLocation();
  const navState = location.state as { productId?: number; suggestedQty?: number; supplierId?: number; purchaseRequest?: PurchaseRequestLite } | null;

  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Purchase | null>(null);
  const [form, setForm] = useState<Purchase>({
    id: 0,
    supplierId: undefined,
    supplierName: '',
    reference: '',
    expectedDate: new Date().toISOString().slice(0, 10),
    requiredDate: '',
    status: 'Pendiente',
    currency: 'ARS',
    exchangeRate: 1,
    total: 0,
    notes: '',
    items: [],
    attachments: [],
  });
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [materials, setMaterials] = useState<{ id: number; name: string; code?: string; cost: number }[]>([]);
  const [products, setProducts] = useState<{ id: number; name: string; sku?: string; cost: number }[]>([]);
  // Pedido interno pendiente elegido para convertir en esta orden (solo al crear).
  const [pendingRequests, setPendingRequests] = useState<PurchaseRequestLite[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequestLite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState<Purchase | null>(null);
  // Filtro por estado de recepción. 'Pendiente por recibir' agrupa Pendiente + Parcial.
  const [receptionFilter, setReceptionFilter] = useState<'Todas' | 'Pendiente por recibir' | 'Parcial' | 'Recibida'>('Todas');

  // Recibir mercadería
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receivePurchase, setReceivePurchase] = useState<Purchase | null>(null);
  const [receiveLines, setReceiveLines] = useState<ReceiveLine[]>([]);
  const [receiveSaving, setReceiveSaving] = useState(false);

  const handleAttachmentSelect = (files: FileList | null) => {
    if (!files) return;
    const pdfs = Array.from(files).filter(f => (f.type || '').includes('pdf'));
    setNewAttachments(prev => [...prev, ...pdfs]);
  };

  const removeAttachment = (name: string) => {
    setNewAttachments(prev => prev.filter(f => f.name !== name));
  };

  const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const resolveAttachmentUrl = (url: string) => {
    if (!url) return '#';
    if (url.startsWith('http')) return url;
    if (API_BASE_URL === '/api') return url;
    // Backend returns urls with /api/ prefix — strip it to avoid doubling
    const path = url.startsWith('/api/') ? url.slice(4) : url;
    return `${API_BASE_URL}${path}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getSuppliers({ page: 1, pageSize: 500, search: '' });
        setSuppliers(res.suppliers || []);
      } catch { setSuppliers([]); }
      try {
        const mats = await api.getMaterials({ page: 1, pageSize: 500 });
        setMaterials((mats.materials || []).map((m: any) => ({ id: m.id, name: m.name, code: m.code, cost: m.cost })));
      } catch { setMaterials([]); }
      try {
        const prods = await api.getProducts({ page: 1, pageSize: 500 });
        setProducts((prods.products || []).map((p: any) => ({ id: p.id, name: p.name, sku: p.sku, cost: p.cost })));
      } catch { setProducts([]); }
      try {
        const reqs = await api.getPurchaseRequests('Pendiente');
        setPendingRequests(reqs.items || []);
      } catch { setPendingRequests([]); }
    })();
  }, []);

  // Vuelca un pedido interno en el form: ítems, fecha requerida y vínculo.
  const applyRequestToForm = (req: PurchaseRequestLite | null) => {
    setSelectedRequest(req);
    if (!req) return;
    setForm(prev => ({
      ...prev,
      requiredDate: req.requiredDate ? req.requiredDate.substring(0, 10) : prev.requiredDate,
      items: (req.items || []).map(it => {
        const mat = it.materialId ? materials.find(m => m.id === it.materialId) : undefined;
        const prod = it.productId ? products.find(p => p.id === it.productId) : undefined;
        return {
          productId: it.productId,
          materialId: it.materialId,
          productName: it.productName || prod?.name || mat?.name || '',
          quantity: it.quantity,
          unitCost: prod?.cost ?? mat?.cost ?? 0,
          ivaRate: DEFAULT_IVA_RATE,
        };
      }),
    }));
  };

  const loadPurchases = async () => {
    try {
      const res = await api.getPurchases();
      setPurchases(res.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar las compras');
      setPurchases([]);
    }
  };

  useEffect(() => { loadPurchases(); }, []);

  const openReceive = (p: Purchase) => {
    setReceivePurchase(p);
    setReceiveLines((p.items || []).filter(it => it.id).map((it, idx) => {
      const pending = Math.max(0, (it.quantity || 0) - (it.receivedQuantity || 0));
      // Por defecto: una fila con lote = referencia + número de ítem, cantidad = pendiente.
      // Las líneas con producto se reciben como stock del producto. Para las demás se
      // pre-completa materialId si el ítem mapea por nombre a una materia prima conocida.
      const guessedMat = it.productId ? undefined : (it.materialId ?? materials.find(m => m.name === it.productName)?.id);
      return {
        purchaseItemId: it.id!,
        materialId: guessedMat,
        rows: [{ quantity: pending, lotNumber: it.lotNumber || `${p.reference}-${idx + 1}`, expirationDate: '' }],
      };
    }));
    setReceiveOpen(true);
  };

  const handleReceiveSubmit = async () => {
    if (!receivePurchase) return;
    // El backend acepta varias entradas con el mismo purchaseItemId: cada fila
    // de lote viaja como una entrada propia (crea su propio lote de stock).
    const payloadItems = receiveLines.flatMap(l =>
      l.rows
        .filter(r => r.quantity > 0)
        .map(r => ({
          purchaseItemId: l.purchaseItemId,
          materialId: l.materialId,
          receivedQuantity: r.quantity,
          lotNumber: r.lotNumber || null,
          expirationDate: r.expirationDate || null,
        })));
    if (payloadItems.length === 0) { setError('Indicá al menos una cantidad recibida'); return; }
    setReceiveSaving(true);
    try {
      const res: any = await api.request({ method: 'POST', url: `/purchases/${receivePurchase.id}/receive`, data: { items: payloadItems } });
      setReceiveOpen(false);
      setReceivePurchase(null);
      // Alarma de vencimiento corto que devuelve el backend (umbral configurable
      // en Notificaciones). Se muestra acá además de dispararse por mail.
      const warnings: string[] = res?.warnings || [];
      if (warnings.length > 0) setError(`⚠ Vencimiento corto en lo recibido — ${warnings.join(' · ')}`);
      await loadPurchases();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al registrar recepción');
    } finally {
      setReceiveSaving(false);
    }
  };

  const [copiedSnackbar, setCopiedSnackbar] = useState(false);

  // Arma el texto del pedido (estilo WhatsApp) y lo copia al portapapeles.
  const handleCopyForSupplier = async (p: Purchase) => {
    const lines = (p.items || []).map(it => `- ${it.productName || it.materialName || 'Ítem'} x ${it.quantity}`).join('\n');
    const greeting = p.supplierName ? `Hola ${p.supplierName}, ` : 'Hola, ';
    const text = `${greeting}te solicito el siguiente pedido:\n${lines}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSnackbar(true);
    } catch {
      setError('No se pudo copiar el pedido al portapapeles');
    }
  };

  const handleDownloadPdf = async (p: Purchase) => {
    try {
      const data = await api.downloadPurchaseOrderPdf(p.id);
      // Forzar el MIME y sanear la referencia (espacios, "/", "&", etc.) para que el
      // navegador respete el nombre y la extensión .pdf.
      const blob = new Blob([data], { type: 'application/pdf' });
      const safeRef = String(p.reference || p.id).replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || String(p.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orden-compra-${safeRef}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revocar recién después de que la descarga arrancó: hacerlo sincrónico puede abortarla.
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      setError('No se pudo descargar el PDF de la orden');
    }
  };

  // "Generar OC" desde la página de pedidos internos: abre el diálogo de creación
  // con el pedido ya aplicado (ítems + fecha requerida + vínculo para convertirlo).
  // Espera a que cargue el catálogo que el pedido necesita para sembrar los costos.
  useEffect(() => {
    if (navState?.purchaseRequest && !prefillApplied) {
      const req = navState.purchaseRequest;
      const needsProducts = (req.items || []).some(it => it.productId);
      const needsMaterials = (req.items || []).some(it => it.materialId);
      if ((needsProducts && products.length === 0) || (needsMaterials && materials.length === 0)) return;
      setEditing(null);
      setNewAttachments([]);
      applyRequestToForm(req);
      setDialogOpen(true);
      setPrefillApplied(true);
    }
  }, [navState, prefillApplied, products, materials]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill form from navigation state (e.g., from Dashboard)
  useEffect(() => {
    if (navState?.productId && materials.length > 0 && !prefillApplied) {
      const material = materials.find(m => m.id === navState.productId);
      if (material) {
        setForm(prev => ({
          ...prev,
          supplierId: navState.supplierId,
          items: [{
            productId: undefined,
            productName: material.name,
            materialId: material.id,
            quantity: navState.suggestedQty || 1,
            unitCost: material.cost || 0,
            ivaRate: DEFAULT_IVA_RATE,
          }],
        }));
        setDialogOpen(true);
        setPrefillApplied(true);
      }
    }
  }, [navState, materials, prefillApplied]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Órdenes de compra</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => { setEditing(null); setNewAttachments([]); setSelectedRequest(null); setForm({
            id: 0,
            supplierId: undefined,
            supplierName: '',
            reference: '',
            expectedDate: new Date().toISOString().slice(0, 10),
            requiredDate: '',
            status: 'Pendiente',
            total: 0,
            notes: '',
            items: [],
            attachments: [],
          }); setDialogOpen(true); }}>Nueva compra</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Typography variant="h6">Órdenes registradas</Typography>
            <TextField
              select
              size="small"
              label="Recepción"
              value={receptionFilter}
              onChange={(e) => setReceptionFilter(e.target.value as typeof receptionFilter)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="Todas">Todas</MenuItem>
              <MenuItem value="Pendiente por recibir">Pendiente por recibir</MenuItem>
              <MenuItem value="Parcial">Recibida parcialmente</MenuItem>
              <MenuItem value="Recibida">Recibida</MenuItem>
            </TextField>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Referencia</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Fecha esperada</TableCell>
                  <TableCell>Fecha requerida</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Recepción</TableCell>
                  <TableCell>Pago</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Pagado</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell>Adjuntos</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.filter((p) => {
                  const rs = p.receptionStatus || 'Pendiente';
                  if (receptionFilter === 'Todas') return true;
                  if (receptionFilter === 'Pendiente por recibir') return rs === 'Pendiente';
                  return rs === receptionFilter;
                }).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      {p.reference}
                      {p.purchaseRequestNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Pedido {p.purchaseRequestNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{p.supplierName || '-'}</TableCell>
                    <TableCell>{fmtDateUtc(p.expectedDate)}</TableCell>
                    <TableCell>
                      {p.requiredDate
                        ? (() => {
                            const overdue = p.receptionStatus !== 'Recibida' && isPastDate(p.requiredDate);
                            return (
                              <Typography variant="body2" color={overdue ? 'error' : undefined} fontWeight={overdue ? 700 : undefined}>
                                {fmtDateUtc(p.requiredDate)}
                              </Typography>
                            );
                          })()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={p.status} color={STATUS_CHIP_COLOR[p.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={p.receptionStatus || 'Pendiente'} color={RECEPTION_CHIP_COLOR[p.receptionStatus || 'Pendiente'] || 'default'} variant={p.receptionStatus === 'Recibida' ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell>{(p as any).paymentMethod || '-'}</TableCell>
                    <TableCell>{(p as any).paymentDueDate ? new Date((p as any).paymentDueDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell align="right">
                      {p.currency && p.currency !== 'ARS'
                        ? <Tooltip title={`≈ $${(p.totalLocal ?? p.total).toLocaleString('es-AR', { maximumFractionDigits: 2 })} a cotización ${p.exchangeRate}`}>
                            <span>{p.currency} {p.total.toFixed(2)}</span>
                          </Tooltip>
                        : <>${p.total.toFixed(2)}</>}
                    </TableCell>
                    <TableCell align="right">${(p.paidAmount || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={Math.max(0, (p.total || 0) - (p.paidAmount || 0)) > 0.5 ? 'warning.main' : 'success.main'}
                        fontWeight={600}
                      >
                        ${Math.max(0, (p.total || 0) - (p.paidAmount || 0)).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {p.items?.length ? p.items.map(it => `${it.productName || it.productId || '-'} x ${it.quantity}`).join(', ') : '-'}
                    </TableCell>
                    <TableCell>{p.notes || '-'}</TableCell>
                    <TableCell>
                      {p.attachments?.length
                        ? p.attachments.map(att => (
                          <Button key={att.id} size="small" component="a" href={resolveAttachmentUrl(att.url)} target="_blank" rel="noreferrer" sx={{ mr: 1 }}>
                            PDF
                          </Button>
                        ))
                        : <Typography variant="body2" color="text.secondary">-</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Copiar el pedido como texto para mandarlo al proveedor (WhatsApp)">
                          <IconButton size="small" onClick={() => handleCopyForSupplier(p)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Descargar PDF de la orden para mandar al proveedor">
                          <IconButton size="small" color="primary" onClick={() => handleDownloadPdf(p)}>
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<PaymentIcon />}
                          onClick={() => setPaymentTarget(p)}
                        >
                          Registrar pago
                        </Button>
                        {p.receptionStatus !== 'Recibida' && (
                          <Button size="small" variant="contained" color="success" startIcon={<LocalShippingIcon />} onClick={() => openReceive(p)}>
                            Recibir
                          </Button>
                        )}
                        <Button size="small" variant="outlined" onClick={() => {
                          setEditing(p);
                          setNewAttachments([]);
                          setSelectedRequest(null);
                          setForm({
                            ...p,
                            expectedDate: p.expectedDate ? p.expectedDate.substring(0, 10) : '',
                            requiredDate: p.requiredDate ? p.requiredDate.substring(0, 10) : '',
                            attachments: p.attachments || [],
                          });
                          setDialogOpen(true);
                        }}>Editar</Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={async () => {
                          if (!window.confirm(`¿Eliminar la orden de compra ${p.reference}?`)) return;
                          setError(null);
                          try {
                            await api.deletePurchase(Number(p.id));
                            await loadPurchases();
                          } catch (e: any) {
                            setError(e?.response?.data?.message || 'No se pudo eliminar la orden de compra');
                          }
                        }}>Eliminar</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {purchases.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Aún no registraste órdenes de compra.</Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{editing ? 'Editar compra' : 'Nueva compra'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {!editing && (
            <Autocomplete
              options={pendingRequests}
              getOptionLabel={(op) => `${op.requestNumber} — ${op.requestedBy || 'sin solicitante'}${op.priority ? ` (${op.priority})` : ''}`}
              value={selectedRequest}
              onChange={(_, val) => applyRequestToForm(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pedido interno (opcional)"
                  helperText="Si elegís un pedido, la orden se crea a partir de sus ítems y el pedido queda convertido. No es obligatorio."
                />
              )}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Referencia" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Proveedor"
                select
                value={form.supplierId ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number(e.target.value);
                  const selected = suppliers.find(s => s.id === value);
                  setForm({
                    ...form,
                    supplierId: value,
                    supplierName: selected?.name || '',
                  });
                }}
                fullWidth
              >
                <MenuItem value="">Seleccione</MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}{s.cuit ? ` (CUIT: ${s.cuit})` : ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
          <TextField label="Fecha esperada" type="date" InputLabelProps={{ shrink: true }} value={form.expectedDate} onChange={(e) => setForm({ ...form, expectedDate: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha requerida"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.requiredDate || ''}
                onChange={(e) => setForm({ ...form, requiredDate: e.target.value })}
                helperText="Para qué día la necesitamos nosotros"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Estado" select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as PurchaseStatus })} fullWidth>
                {['Pendiente','En proceso','Recibida'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Moneda" select fullWidth
                value={form.currency || 'ARS'}
                onChange={(e) => {
                  const cur = e.target.value;
                  setForm({ ...form, currency: cur, exchangeRate: cur === 'ARS' ? 1 : (form.exchangeRate && form.exchangeRate > 1 ? form.exchangeRate : undefined) });
                }}
              >
                {['ARS','USD','EUR'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            {form.currency && form.currency !== 'ARS' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label={`Cotización (pesos por ${form.currency})`}
                  type="number" fullWidth
                  value={form.exchangeRate ?? ''}
                  onChange={(e) => setForm({ ...form, exchangeRate: e.target.value === '' ? undefined : Number(e.target.value) })}
                  helperText={
                    form.exchangeRate && form.exchangeRate > 0
                      ? `Total en pesos: $${(form.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitCost) || 0) * (1 + (it.ivaRate ?? DEFAULT_IVA_RATE) / 100), 0) * form.exchangeRate).toLocaleString('es-AR', { maximumFractionDigits: 2 })}`
                      : 'Sin cotización se guarda como pesos y los costos quedan mal'
                  }
                  error={!form.exchangeRate || form.exchangeRate <= 0}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={PURCHASE_TYPES}
                value={form.type || ''}
                onChange={(_, val) => setForm({ ...form, type: (val as string) || undefined })}
                onInputChange={(_, val) => setForm({ ...form, type: val || undefined })}
                renderInput={(params) => <TextField {...params} label="Tipo" placeholder="Reposición / Fason / ..." fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Condición de pago"
                select
                value={(form as any).paymentMethod || 'Contado'}
                onChange={(e) => {
                  const method = e.target.value;
                  if (method === 'Contado') {
                    setForm({ ...form, ...(({ paymentMethod: method, paymentDueDate: undefined } as any)) } as any);
                  } else {
                    // Pre-fill with supplier's payment term days
                    const sup = suppliers.find(s => s.id === form.supplierId);
                    const days = sup?.paymentTermDays || 30;
                    const dueDate = new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
                    setForm({ ...form, ...(({ paymentMethod: method, paymentDueDate: dueDate } as any)) } as any);
                  }
                }}
                fullWidth
              >
                {['Contado', 'Diferido', 'Transferencia', 'Cheque'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            {(form as any).paymentMethod && (form as any).paymentMethod !== 'Contado' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Fecha de vencimiento"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={(form as any).paymentDueDate || ''}
                  onChange={(e) => setForm({ ...form, ...(({ paymentDueDate: e.target.value } as any)) } as any)}
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              {(() => {
                const inst = form.installments || [];
                const total = form.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitCost) || 0) * (1 + (it.ivaRate ?? DEFAULT_IVA_RATE) / 100), 0);
                const totalPct = inst.reduce((acc, i) => acc + (Number(i.percentage) || 0), 0);
                const updateInst = (next: PurchasePaymentInstallment[]) => setForm({ ...form, installments: next.map((it, idx) => ({ ...it, sequence: idx + 1 })) });
                const addInst = () => {
                  const remaining = Math.max(0, 100 - totalPct);
                  const lastDays = inst.length > 0 ? inst[inst.length - 1].defermentDays + 30 : 0;
                  updateInst([...inst, { sequence: inst.length + 1, percentage: remaining || 0, paymentMethod: 'Transferencia', defermentDays: lastDays }]);
                };
                const removeInst = (idx: number) => updateInst(inst.filter((_, i) => i !== idx));
                const setField = (idx: number, field: keyof PurchasePaymentInstallment, value: any) => {
                  const next = inst.map((it, i) => i === idx ? { ...it, [field]: value } : it);
                  updateInst(next);
                };
                return (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Pago diferido / cuotas</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {inst.length > 0 && (
                          <Typography variant="caption" color={Math.abs(totalPct - 100) > 0.01 ? 'error.main' : 'text.secondary'}>
                            Total: {totalPct}%
                          </Typography>
                        )}
                        <Button size="small" onClick={addInst}>Agregar cuota</Button>
                      </Stack>
                    </Stack>
                    {inst.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Sin cronograma. Para diferir el pago (ej. 30% contado, 30% cheque a 30 días, 40% a 60), agregá cuotas.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 50 }}>#</TableCell>
                            <TableCell sx={{ width: 110 }}>%</TableCell>
                            <TableCell sx={{ width: 150 }}>Monto</TableCell>
                            <TableCell sx={{ width: 200 }}>Método</TableCell>
                            <TableCell sx={{ width: 130 }}>Días dif.</TableCell>
                            <TableCell sx={{ width: 60 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inst.map((it, idx) => {
                            const amount = Math.round(total * (Number(it.percentage) || 0) / 100 * 100) / 100;
                            return (
                              <TableRow key={idx}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                  <TextField size="small" type="number" inputProps={{ min: 0, max: 100, step: 0.01 }} value={it.percentage}
                                    onChange={e => setField(idx, 'percentage', e.target.value === '' ? 0 : Number(e.target.value))} sx={{ width: 100 }} />
                                </TableCell>
                                <TableCell>${amount.toFixed(2)}</TableCell>
                                <TableCell>
                                  <TextField select size="small" value={it.paymentMethod}
                                    onChange={e => setField(idx, 'paymentMethod', e.target.value)} sx={{ minWidth: 180 }}>
                                    {['Efectivo', 'Cheque', 'Transferencia', 'Tarjeta', 'CuentaCorriente', 'Otro'].map(m => (
                                      <MenuItem key={m} value={m}>{m}</MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" type="number" inputProps={{ min: 0, step: 1 }} value={it.defermentDays}
                                    onChange={e => setField(idx, 'defermentDays', e.target.value === '' ? 0 : Number(e.target.value))} sx={{ width: 100 }} />
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => removeInst(idx)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Notas" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="outlined" component="label">
                  Adjuntar PDF
                  <input hidden type="file" accept="application/pdf" multiple onChange={(e) => handleAttachmentSelect(e.target.files)} />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Se adjuntarán al guardar (solo PDF).
                </Typography>
              </Stack>
              {form.attachments && form.attachments.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Adjuntos existentes</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {form.attachments.map(att => (
                      <Button key={att.id} size="small" component="a" href={resolveAttachmentUrl(att.url)} target="_blank" rel="noreferrer">
                        {att.fileName || 'PDF'}
                      </Button>
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">Si agregas nuevos adjuntos, reemplazarán los existentes.</Typography>
                </Box>
              )}
              {newAttachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Adjuntos nuevos</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {newAttachments.map(file => (
                      <Button key={file.name} size="small" onClick={() => removeAttachment(file.name)}>
                        {file.name} (quitar)
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Items</Typography>
              <Button variant="outlined" size="small" onClick={() => setForm({ ...form, items: [...form.items, { source: 'product', productId: undefined, productName: '', quantity: 1, unitCost: 0, ivaRate: DEFAULT_IVA_RATE }] })}>Agregar fila</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 140 }}>Origen</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Costo unitario</TableCell>
                  <TableCell align="right">IVA %</TableCell>
                  <TableCell>Lote</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.items.map((it, idx) => {
                  const source = itemSource(it);
                  const mat = materials.find(m => m.id === it.materialId) || null;
                  const prod = products.find(p => p.id === it.productId) || null;
                  const subtotal = (it.quantity || 0) * (it.unitCost || 0);
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={source}
                          onChange={(e) => {
                            const items = [...form.items];
                            items[idx] = { ...items[idx], source: e.target.value as PurchaseItem['source'], productId: undefined, materialId: undefined, productName: '' };
                            setForm({ ...form, items });
                          }}
                        >
                          <MenuItem value="product">Producto</MenuItem>
                          <MenuItem value="material">Materia prima</MenuItem>
                          <MenuItem value="free">Texto libre</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        {source === 'product' && (
                          <Autocomplete
                            options={products}
                            getOptionLabel={(op) => `${op.sku ? `${op.sku} — ` : ''}${op.name}`}
                            value={prod}
                            onChange={(_, val) => {
                              const items = [...form.items];
                              items[idx] = { ...items[idx], productId: val?.id, materialId: undefined, productName: val?.name || '', unitCost: val?.cost || items[idx].unitCost };
                              setForm({ ...form, items });
                            }}
                            renderInput={(params) => <TextField {...params} label="Producto" />}
                          />
                        )}
                        {source === 'material' && (
                          <Autocomplete
                            options={materials}
                            getOptionLabel={(op) => `${op.code || ''} — ${op.name}`}
                            value={mat}
                            onChange={(_, val) => {
                              const items = [...form.items];
                              items[idx] = { ...items[idx], productId: undefined, materialId: val?.id, productName: val?.name || '', unitCost: val?.cost || items[idx].unitCost };
                              setForm({ ...form, items });
                            }}
                            renderInput={(params) => <TextField {...params} label="Materia prima" />}
                          />
                        )}
                        {source === 'free' && (
                          <TextField
                            fullWidth
                            label="Descripción"
                            placeholder="Ej: rollo de cocina, paletas..."
                            value={it.productName || ''}
                            onChange={(e) => {
                              const items = [...form.items];
                              items[idx] = { ...items[idx], productId: undefined, materialId: undefined, productName: e.target.value };
                              setForm({ ...form, items });
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>
                        <TextField
                          type="number"
                          size="small"
                          value={it.quantity}
                          onChange={(e) => {
                            const items = [...form.items];
                            items[idx] = { ...items[idx], quantity: Math.max(0, Number(e.target.value)) };
                            setForm({ ...form, items });
                          }}
                          inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 180 }}>
                        <TextField
                          type="number"
                          size="small"
                          value={it.unitCost}
                          onChange={(e) => {
                            const items = [...form.items];
                            items[idx] = { ...items[idx], unitCost: Math.max(0, Number(e.target.value)) };
                            setForm({ ...form, items });
                          }}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 110 }}>
                        <TextField
                          select
                          size="small"
                          value={it.ivaRate ?? DEFAULT_IVA_RATE}
                          onChange={(e) => {
                            const items = [...form.items];
                            items[idx] = { ...items[idx], ivaRate: Number(e.target.value) };
                            setForm({ ...form, items });
                          }}
                          sx={{ minWidth: 90 }}
                        >
                          {IVA_RATES.map((r) => <MenuItem key={r} value={r}>{r}%</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <TextField
                          size="small"
                          placeholder="Nº de lote"
                          value={it.lotNumber ?? ''}
                          onChange={(e) => {
                            const items = [...form.items];
                            items[idx] = { ...items[idx], lotNumber: e.target.value };
                            setForm({ ...form, items });
                          }}
                          sx={{ minWidth: 130 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>
                        <Typography variant="body2">${subtotal.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>
                        <IconButton color="error" onClick={() => {
                          const items = form.items.filter((_, i) => i !== idx);
                          setForm({ ...form, items });
                        }}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {form.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">Agrega filas para cargar la orden.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          {(() => {
            const neto = form.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitCost) || 0), 0);
            const iva = form.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitCost) || 0) * ((it.ivaRate ?? DEFAULT_IVA_RATE) / 100), 0);
            return (
              <Stack direction="row" justifyContent="flex-end" spacing={3}>
                <Typography variant="body2" color="text.secondary">Subtotal: ${neto.toFixed(2)}</Typography>
                <Typography variant="body2" color="text.secondary">IVA: ${iva.toFixed(2)}</Typography>
                <Typography variant="subtitle1" fontWeight={700}>Total: ${(neto + iva).toFixed(2)}</Typography>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setNewAttachments([]); }}>Cancelar</Button>
          <Button variant="contained" onClick={async () => {
            setError(null);
            const total = form.items.reduce(
              (acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitCost) || 0) * (1 + (it.ivaRate ?? DEFAULT_IVA_RATE) / 100),
              0,
            );
            try {
              const attachmentsPayload = newAttachments.length
                ? await Promise.all(newAttachments.map(async (file) => ({
                    fileName: file.name,
                    contentType: file.type || 'application/pdf',
                    base64: await fileToBase64(file),
                  })))
                : undefined;
              const payload = {
                reference: form.reference,
                supplierId: form.supplierId,
                expectedDate: form.expectedDate,
                requiredDate: form.requiredDate || undefined,
                purchaseRequestId: !editing && selectedRequest ? selectedRequest.id : undefined,
                status: form.status,
                total,
                notes: form.notes,
                paymentMethod: (form as any).paymentMethod || undefined,
                paymentDueDate: (form as any).paymentDueDate || undefined,
                type: form.type || undefined,
                installments: (form.installments && form.installments.length > 0)
                  ? form.installments.map((i, idx) => ({
                      sequence: idx + 1,
                      percentage: Number(i.percentage) || 0,
                      paymentMethod: i.paymentMethod || 'Transferencia',
                      defermentDays: Number(i.defermentDays) || 0,
                    }))
                  : undefined,
                items: form.items.map(it => ({
                  id: it.id,
                  productId: it.productId,
                  productName: it.productName,
                  materialId: it.materialId,
                  quantity: Number(it.quantity) || 0,
                  unitCost: Number(it.unitCost) || 0,
                  ivaRate: it.ivaRate ?? DEFAULT_IVA_RATE,
                  lotNumber: it.lotNumber?.trim() || undefined,
                })),
                attachments: attachmentsPayload && attachmentsPayload.length > 0 ? attachmentsPayload : undefined,
              };
              if (editing?.id) {
                await api.updatePurchase(Number(editing.id), payload);
              } else {
                await api.createPurchase(payload);
              }
              setDialogOpen(false);
              await loadPurchases();
              setNewAttachments([]);
              if (selectedRequest) {
                setSelectedRequest(null);
                try {
                  const reqs = await api.getPurchaseRequests('Pendiente');
                  setPendingRequests(reqs.items || []);
                } catch { /* la lista se refresca al reabrir */ }
              }
            } catch (e: any) {
              setError(e?.response?.data?.message || (editing ? 'No se pudo guardar la orden de compra' : 'No se pudo crear la orden de compra'));
            }
          }}>{editing ? 'Guardar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      <RegisterPaymentDialog
        open={!!paymentTarget}
        title={paymentTarget ? `Registrar pago — Compra ${paymentTarget.reference}` : 'Registrar pago'}
        total={paymentTarget?.total || 0}
        paidAmount={paymentTarget?.paidAmount || 0}
        onClose={() => setPaymentTarget(null)}
        onConfirm={async (payload) => {
          if (!paymentTarget) return;
          await api.registerPurchasePayment(Number(paymentTarget.id), payload);
          await loadPurchases();
        }}
      />

      <Dialog open={receiveOpen} onClose={() => setReceiveOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Recibir mercadería · {receivePurchase?.reference}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cargá lo que llegó de cada ítem. Si un mismo ítem vino en <b>varios lotes</b> (o con
            distintos vencimientos), agregá una fila por lote. Un ítem en cero no se toca.
          </Typography>
          {receiveLines.map((line, idx) => {
            const item = receivePurchase?.items.find(it => it.id === line.purchaseItemId);
            const matValue = materials.find(m => m.id === line.materialId) || null;
            const pending = Math.max(0, (item?.quantity ?? 0) - (item?.receivedQuantity ?? 0));
            const totalNow = line.rows.reduce((s, r) => s + (r.quantity || 0), 0);
            return (
              <Box key={line.purchaseItemId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} sx={{ mb: 1 }}>
                  <Box>
                    <Typography fontWeight={600}>{item?.productName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pedido: {item?.quantity ?? 0} · Ya recibido: {item?.receivedQuantity ?? 0} · Pendiente: {pending}
                    </Typography>
                  </Box>
                  {item?.productId ? (
                    <Typography variant="body2" color="success.main" fontWeight={600}>Producto (stock propio)</Typography>
                  ) : (
                    <Autocomplete
                      size="small"
                      options={materials}
                      getOptionLabel={(op) => `${op.code || ''} — ${op.name}`}
                      value={matValue}
                      onChange={(_, val) => setReceiveLines(prev => prev.map((l, i) => i === idx ? { ...l, materialId: val?.id } : l))}
                      renderInput={(params) => <TextField {...params} placeholder="Materia prima (vacío = sin stock)" />}
                      sx={{ minWidth: 260 }}
                    />
                  )}
                </Stack>
                {line.rows.map((row, rIdx) => (
                  <Stack key={rIdx} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Cantidad"
                      value={row.quantity}
                      onChange={e => setReceiveLines(prev => prev.map((l, i) => i === idx ? { ...l, rows: l.rows.map((r, j) => j === rIdx ? { ...r, quantity: Math.max(0, Number(e.target.value || 0)) } : r) } : l))}
                      inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      label="Lote"
                      value={row.lotNumber}
                      onChange={e => setReceiveLines(prev => prev.map((l, i) => i === idx ? { ...l, rows: l.rows.map((r, j) => j === rIdx ? { ...r, lotNumber: e.target.value } : r) } : l))}
                      sx={{ flex: 1, minWidth: 150 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="Vencimiento"
                      InputLabelProps={{ shrink: true }}
                      value={row.expirationDate}
                      onChange={e => setReceiveLines(prev => prev.map((l, i) => i === idx ? { ...l, rows: l.rows.map((r, j) => j === rIdx ? { ...r, expirationDate: e.target.value } : r) } : l))}
                      sx={{ width: 175 }}
                    />
                    <Tooltip title={line.rows.length === 1 ? 'Poné la cantidad en 0 para no recibir este ítem' : 'Quitar fila'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={line.rows.length === 1}
                          onClick={() => setReceiveLines(prev => prev.map((l, i) => i === idx ? { ...l, rows: l.rows.filter((_, j) => j !== rIdx) } : l))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setReceiveLines(prev => prev.map((l, i) => i === idx ? { ...l, rows: [...l.rows, { quantity: 0, lotNumber: '', expirationDate: '' }] } : l))}
                  >
                    Agregar otro lote
                  </Button>
                  {line.rows.length > 1 && (
                    <Typography variant="caption" color={totalNow > pending ? 'warning.main' : 'text.secondary'}>
                      Total a recibir ahora: {totalNow}{totalNow > pending ? ` (${(totalNow - pending)} más que lo pendiente)` : ''}
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
          {receiveLines.length === 0 && (
            <Typography variant="body2" color="text.secondary">Sin ítems disponibles.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={receiveSaving} onClick={handleReceiveSubmit}>
            {receiveSaving ? 'Guardando…' : 'Confirmar recepción'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copiedSnackbar}
        autoHideDuration={2500}
        onClose={() => setCopiedSnackbar(false)}
        message="Pedido copiado al portapapeles"
      />
    </Box>
  );
};
